/** @jsxImportSource @emotion/react */
import { Header } from '@quickbit/backoffice-core-frontend';
import {
  Table,
  Typography,
  useTheme,
  useForm,
  Pagination,
  SearchInput,
  Icon,
  UtilityButton,
  Modal,
  Input,
} from '@quickbit/qb-design-system';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSessionStorage } from 'react-use';
import { useSearch, Filters } from '../useSitesApi';

const PAGE_SIZE = 16;

export const SiteList = () => {
  const { spacing, palette } = useTheme();
  const navigate = useNavigate();
  const [showNewModal, setShowNewModal] = useState(false);
  const [newDomain, setNewDomain] = useState('');

  const [filterStore, setFilterStore] = useSessionStorage<{
    filters: Filters;
    pagination: {
      page: number;
      size: number;
    };
  } | null>('siteThemeFilterStore');

  const [page, setPage] = useState(filterStore?.pagination.page || 1);

  const { fields, setters } = useForm<Filters>(
    {
      domain: filterStore?.filters.domain || undefined,
    },
    {}
  );

  useEffect(() => {
    setFilterStore({
      filters: fields,
      pagination: { page, size: PAGE_SIZE },
    });
  }, [fields, page, setFilterStore]);

  const { data, isLoading, refetch } = useSearch(fields, {
    page: fields.domain ? 1 : page,
    size: PAGE_SIZE,
  });
  const headers = [{ label: 'Domain', cellKey: 'domain', sort: false }];
  const rows = useMemo(() => {
    if (!data) return [];

    // A workaround to reset pagination if the data rows are less than the page size
    setPage((state) => (data.aggregatedData.count <= PAGE_SIZE ? 1 : state));

    return Object.values(data.sites).map((site) => ({
      value: {
        domain: site.domain,
      },
    }));
  }, [data]);

  const handleRowClicked = (row: any) => {
    if (!data) return;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
    void navigate(row.domain);
  };

  const handleResetFilters = () => {
    setPage(1);
    setters.set({
      domain: undefined,
    });
  };

  return (
    <>
      <Header>
        <div
          css={{
            display: 'grid',
            gridTemplateColumns: '1fr repeat(1, auto)',
            alignItems: 'center',
            gap: spacing.xxs,
          }}
        >
          <Typography variant="subtitle">Skinning Sites</Typography>
          <UtilityButton
            text="Create new site"
            onClick={() => setShowNewModal(true)}
            css={{ marginLeft: spacing.xxs }}
          />
        </div>
      </Header>
      <div
        css={{
          display: 'grid',
          gridTemplateColumns: 'auto auto auto auto auto auto',
          gap: spacing.xxxs,
          alignItems: 'center',
          zIndex: 1,
        }}
      >
        <SearchInput
          placeholder="Domain"
          value={fields.domain ?? ''}
          onChange={(e) => setters.domain?.set(e.target.value)}
        />
      </div>
      <div
        css={{
          marginTop: spacing.xxs,
          display: 'grid',
          gridTemplateColumns: '8fr 1fr',
          textWrap: 'nowrap',
        }}
      >
        <div
          css={{
            display: 'flex',
            alignItems: 'center',
            gap: spacing.xxxs,
            flexWrap: 'wrap',
          }}
        >
          <UtilityButton
            variant="text"
            text="Reset filters"
            onClick={handleResetFilters}
          />
          <UtilityButton
            variant="secondary"
            text="Refresh"
            onClick={() => refetch()}
            disabled={isLoading}
          />
          {isLoading && (
            <Icon
              name="spinner"
              className={{ svg: { color: palette.grey.default } }}
            />
          )}
        </div>
      </div>
      {data && (
        <>
          <div css={{ marginTop: spacing.xxs }}>
            <Table
              headers={headers}
              rows={rows}
              onRowClick={handleRowClicked}
            />
          </div>
          <div css={{ marginTop: spacing.xxs }}>
            <Pagination
              currentPage={page}
              onChange={setPage}
              numberOfElements={data.aggregatedData.count}
              pageSize={PAGE_SIZE}
            />
          </div>
        </>
      )}
      {showNewModal && (
        <Modal
          title="Create New Site"
          closeModal={() => setShowNewModal(false)}
          primaryButtonProps={{
            text: 'Create',
            onClick: () => {
              void navigate(newDomain);
            },
          }}
        >
          <Input
            label="Domain"
            value={newDomain}
            onChange={(e) => setNewDomain(e.target.value)}
          />
        </Modal>
      )}
    </>
  );
};
